import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const stepKeys = ["customer", "product", "access", "limits", "review"] as const;

export type LicenseCreationStep = (typeof stepKeys)[number];

export function LicenseCreationProgress({
  currentStep,
  onStepChange,
  disabled = false,
}: {
  currentStep: number;
  onStepChange: (step: number) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <nav aria-label={t("uxWizard.progressLabel", "License setup progress")}>
      <ol className="grid grid-cols-5 gap-1 sm:gap-2">
        {stepKeys.map((key, index) => {
          const current = index === currentStep;
          const complete = index < currentStep;
          const available = index <= currentStep;
          return (
            <li key={key} className="min-w-0">
              <button
                type="button"
                disabled={disabled || !available}
                onClick={() => onStepChange(index)}
                aria-current={current ? "step" : undefined}
                className={cn(
                  "flex min-h-14 w-full flex-col items-center gap-1 rounded-md border px-1.5 py-2 text-center text-xs transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-not-allowed disabled:opacity-45 sm:min-h-0 sm:flex-row sm:items-center sm:gap-2 sm:px-3 sm:text-left",
                  current && "border-ink bg-panel-raised text-ink",
                  complete && "border-line bg-panel text-ink hover:bg-panel-raised",
                  !current && !complete && "border-line bg-transparent text-muted",
                )}
              >
                <span className={cn(
                  "flex size-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-medium",
                  current && "border-ink bg-ink text-canvas",
                  complete && "border-control text-ink",
                  !current && !complete && "border-control text-muted",
                )}>
                  {complete ? <Check className="size-3" aria-hidden="true" /> : index + 1}
                </span>
                <span className="min-w-0 wrap-break-word leading-4">{t(`uxWizard.steps.${key}`, key)}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function LicenseCreationStep({
  active,
  children,
  showAll = false,
  id,
}: {
  active: boolean;
  children: React.ReactNode;
  showAll?: boolean;
  id?: string;
}) {
  return <section id={id} hidden={!active && !showAll}>{children}</section>;
}

export function LicenseCreationNavigation({
  currentStep,
  onBack,
  onNext,
  disabled = false,
}: {
  currentStep: number;
  onBack: () => void;
  onNext: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const finalStep = currentStep === stepKeys.length - 1;

  if (finalStep) return null;

  return (
    <div className="flex items-center justify-between border-t border-line pt-4">
      {currentStep > 0 ? (
        <Button type="button" variant="outline" onClick={onBack} disabled={disabled}>
          {t("common.back", "Back")}
        </Button>
      ) : <span />}
      <Button type="button" variant="primary" onClick={onNext} disabled={disabled}>
        {t("uxWizard.next", "Continue")}
      </Button>
    </div>
  );
}
